import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Sparkles, ArrowRight, Upload, MousePointerClick, Palette, Wand2,
  Calculator, ChevronDown, Palette as PaletteIcon, Ruler, MessagesSquare,
} from "lucide-react";
import { track } from "@/lib/analytics/track";
import { BeforeAfterSlider } from "@/components/studio/BeforeAfterSlider";
import { RevealOnScroll } from "@/components/site/RevealOnScroll";
import { EstimatePrompt } from "@/components/site/EstimatePrompt";
import heroAsset from "@/assets/mp/hero1.jpg.asset.json";
import beforeAssetJson from "@/assets/before-after-living-before.jpg.asset.json";
import afterAssetJson from "@/assets/before-after-living-after.jpg.asset.json";
const beforeAsset = beforeAssetJson.url;
const afterAsset = afterAssetJson.url;
import michaelOwner from "@/assets/michael-owner.png.asset.json";
import { PROJECTS } from "@/data/portfolio";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Michael's Painting — See Your Home Before You Paint It" },
      { name: "description", content: "Upload a photo, preview real Sherwin-Williams colors with our AI Paint Studio, and get an instant painting estimate. Wisconsin's modern painting contractor." },
      { property: "og:title", content: "Michael's Painting — See Your Home Before You Paint It" },
      { property: "og:description", content: "Upload a photo, preview real Sherwin-Williams colors with our AI Paint Studio, and get an instant painting estimate. Wisconsin's modern painting contractor." },
      { property: "og:url", content: "https://michaelspaintingwi.com/" },
      { property: "og:image", content: heroAsset.url },
    ],
    links: [
      { rel: "canonical", href: "https://michaelspaintingwi.com/" },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <div className="bg-noir text-ivory selection:bg-gold selection:text-noir">
      <Hero />
      <StatBand />
      <SurfaceMastery />
      <StudioBand />
      <BeforeAfter />
      <SelectedWorks />
      <MeetMichael />
      <TestimonialWarranty />
      <EstimateBand />
      <FinalCTA />
      <EstimatePrompt />
    </div>
  );
}

/* ------------------------------ Primitives ------------------------------ */
function SectionMarker({ number, label }: { number: string; label: string }) {
  return (
    <div className="flex items-baseline gap-4">
      <span className="font-serif italic text-gold text-2xl leading-none">{number}</span>
      <span className="h-px flex-none w-8 bg-gold/60" />
      <span className="uppercase tracking-[0.35em] text-[10px] text-gold">{label}</span>
    </div>
  );
}

function GoldCta({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="group relative inline-flex items-center gap-3 px-9 py-4 rounded-full text-noir font-semibold text-[11px] uppercase tracking-[0.28em] overflow-hidden isolate transition-[transform,box-shadow] duration-500 ease-out hover:-translate-y-0.5 shadow-[0_10px_30px_-12px_oklch(0.74_0.11_82/0.55),inset_0_1px_0_0_oklch(0.94_0.06_88/0.9)] hover:shadow-[0_18px_50px_-14px_oklch(0.74_0.11_82/0.7),inset_0_1px_0_0_oklch(0.96_0.06_88/1)]"
    >
      <span
        aria-hidden
        className="absolute inset-0 -z-10 rounded-full bg-[linear-gradient(135deg,oklch(0.88_0.09_88)_0%,oklch(0.78_0.13_82)_45%,oklch(0.62_0.11_78)_100%)]"
      />
      <span
        aria-hidden
        className="absolute inset-0 -z-10 rounded-full ring-1 ring-inset ring-[oklch(0.96_0.06_88/0.55)]"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-y-2 -left-1/3 w-1/3 rotate-12 bg-[linear-gradient(90deg,transparent,oklch(1_0_0/0.55),transparent)] blur-md opacity-0 group-hover:opacity-100 group-hover:translate-x-[420%] transition-all duration-[900ms] ease-out"
      />
      <span className="relative flex items-center gap-2.5">
        {children}
        <span aria-hidden className="inline-block translate-x-0 transition-transform duration-500 group-hover:translate-x-1">→</span>
      </span>
    </Link>
  );
}

function GhostGoldCta({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="group relative inline-block"
    >
      <span aria-hidden className="absolute -inset-1 rounded-full bg-gold blur-2xl opacity-10 group-hover:opacity-25 transition-opacity duration-500" />
      <span className="relative inline-flex items-center gap-2.5 px-9 py-4 rounded-full bg-noir/60 backdrop-blur-sm border border-gold/60 text-gold uppercase tracking-[0.28em] font-semibold text-[11px] transition-all duration-500 group-hover:border-gold-hi group-hover:bg-gold group-hover:text-noir group-hover:-translate-y-0.5 group-hover:shadow-[0_14px_40px_-14px_oklch(0.74_0.11_82/0.6)]">
        {children}
        <span aria-hidden className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
      </span>
    </Link>
  );
}

/* -------------------------------- Hero -------------------------------- */
function Hero() {
  return (
    <section className="relative min-h-[92vh] flex flex-col items-center justify-center px-6 overflow-hidden">
      <div className="absolute inset-0 opacity-40 grayscale contrast-125">
        <img
          src={heroAsset.url}
          alt=""
          className="w-full h-full object-cover"
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-noir/85 via-noir/40 to-noir" />
      </div>

      <RevealOnScroll className="relative z-10 text-center max-w-5xl mx-auto space-y-8 pt-24 pb-16">
        <div className="flex items-center justify-center gap-4">
          <div className="h-px w-12 bg-gold" />
          <span className="text-gold tracking-[0.4em] text-[10px] sm:text-xs uppercase font-medium">Established in Wisconsin</span>
          <div className="h-px w-12 bg-gold" />
        </div>

        <h1 className="font-serif italic leading-[0.95] text-[3.4rem] sm:text-8xl lg:text-[9rem]">
          Michael's <span className="text-gold">Painting</span>
        </h1>

        <p className="max-w-2xl mx-auto text-lg md:text-xl text-ivory/60 font-light leading-relaxed">
          Elevating residential and commercial spaces through the precision of master-class brushwork, artisanal finishes, and thirty years of Wisconsin craftsmanship.
        </p>

        <div className="pt-4 flex flex-col sm:flex-row gap-5 justify-center items-center">
          <GoldCta to="/estimate">
            <Calculator className="size-4" />
            Request an Estimate
          </GoldCta>
          <Link
            to="/studio"
            className="group inline-flex items-center gap-2 text-ivory/80 hover:text-gold transition-colors text-xs uppercase tracking-[0.3em]"
          >
            <Sparkles className="size-3.5" />
            Try the AI Paint Studio
            <ArrowRight className="size-3.5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </RevealOnScroll>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
        <div className="w-px h-16 bg-gradient-to-b from-transparent to-gold" />
        <ChevronDown className="size-3 text-gold/70" />
      </div>
    </section>
  );
}

/* ------------------------------ Stat Band ----------------------------- */
function StatBand() {
  const stats = [
    { k: "30+", v: "Years in Wisconsin" },
    { k: "1,700+", v: "SW Colors live" },
    { k: "3-Year", v: "Workmanship warranty" },
    { k: "Free", v: "Photo-based estimates" },
  ];
  return (
    <section className="border-y border-gold/15 bg-noir-2/60">
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-gold/15">
        {stats.map((s) => (
          <div key={s.v} className="px-6 py-8 sm:py-10 text-left">
            <div className="font-serif italic text-3xl sm:text-4xl text-gold leading-none">{s.k}</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-ivory/50 mt-3">{s.v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* --------------------------- Surface Mastery -------------------------- */
function SurfaceMastery() {
  const tools = [
    {
      to: "/studio" as const,
      search: undefined,
      Icon: PaletteIcon,
      category: "Color Visualizer",
      title: "Visualize Your Colors",
      body: "Upload a photo of your room or exterior and preview Sherwin-Williams colors directly on your property before making a final decision.",
      action: "Open Paint Studio",
      aria: "Open AI Paint Studio",
      event: "homepage_paint_studio_clicked",
    },
    {
      to: "/estimate" as const,
      search: undefined,
      Icon: Ruler,
      category: "Project Pricing",
      title: "Build Your Estimate",
      body: "Answer a few questions about your project and receive a preliminary painting price range based on the surfaces and work selected.",
      action: "Start Estimate",
      aria: "Start Painting Estimate",
      event: "homepage_estimate_builder_clicked",
    },
    {
      to: "/contact" as const,
      search: { type: "consultation" } as const,
      Icon: MessagesSquare,
      category: "Professional Review",
      title: "Request a Consultation",
      body: "Send your project details, selected colors, photos, and estimate information directly to Michael's Painting for professional review.",
      action: "Request Consultation",
      aria: "Request Painting Consultation",
      event: "homepage_consultation_clicked",
    },
  ];
  return (
    <section className="py-24 sm:py-40 px-6 max-w-7xl mx-auto">
      <RevealOnScroll className="flex flex-col md:flex-row justify-between items-end mb-16 sm:mb-20 gap-8">
        <div className="space-y-5">
          <SectionMarker number="01" label="Plan Your Project" />
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight leading-[0.95]">
            Plan Your <span className="italic text-gold">Transformation</span>
          </h2>
        </div>
        <p className="max-w-md text-ivory/55 text-sm leading-relaxed">
          Explore your colors, receive an initial project price range, and send your selections directly to Michael's Painting for professional review.
        </p>
      </RevealOnScroll>

      <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10 motion-safe:transition-colors">
        {tools.map((t, i) => {
          const Icon = t.Icon;
          return (
            <Link
              key={t.title}
              to={t.to}
              search={t.search as never}
              onClick={() =>
                track.event(t.event, { source: "homepage", card_position: i + 1 })
              }
              aria-label={t.aria}
              className={`group relative flex flex-col p-10 sm:p-12 outline-none motion-safe:transition-[transform,background-color,border-color] duration-300 ease-out hover:bg-noir-2 hover:-translate-y-[3px] focus-visible:-translate-y-[3px] focus-visible:bg-noir-2 hover:border-gold/50 focus-visible:border-gold/60 focus-visible:ring-1 focus-visible:ring-gold/60 ${
                i !== 2 ? "md:border-r border-white/10" : ""
              } ${i !== tools.length - 1 ? "border-b md:border-b-0 border-white/10" : ""}`}
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-serif text-gold text-xs tracking-[0.3em] uppercase">
                  {t.category}
                </span>
                <Icon className="size-5 text-gold/70 group-hover:text-gold transition-colors" strokeWidth={1.25} />
              </div>
              <h3 className="font-serif italic text-3xl sm:text-4xl mb-5">{t.title}</h3>
              <p className="text-ivory/55 font-light leading-relaxed text-sm">{t.body}</p>
              <div className="mt-auto pt-10 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold/70 group-hover:text-gold transition-colors">
                {t.action}
                <ArrowRight className="size-3 motion-safe:transition-transform duration-300 group-hover:translate-x-1.5 group-focus-visible:translate-x-1.5" />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/* --------------------------- AI Studio band --------------------------- */
function StudioBand() {
  const steps = [
    { icon: Upload, title: "Upload a photo", body: "Snap a wall, room, or exterior. JPG or PNG up to 20 MB." },
    { icon: MousePointerClick, title: "Pick the surface", body: "AI auto-detects walls, trim, cabinets, ceilings — refine with brush or lasso." },
    { icon: Palette, title: "Apply real colors", body: "Browse 1,700+ Sherwin-Williams colors with live HEX, LRV, and collection filters." },
    { icon: Wand2, title: "Render & compare", body: "Photorealistic previews with saved versions and side-by-side sliders." },
    { icon: Calculator, title: "Send to estimate", body: "One click ports your design into our instant estimate calculator." },
  ];
  return (
    <section className="bg-noir-2 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-24 sm:py-40">
        <RevealOnScroll className="max-w-2xl mb-16 space-y-5">
          <SectionMarker number="02" label="AI Paint Studio" />
          <h2 className="font-serif text-4xl sm:text-6xl leading-[1.02]">
            Design the room. <span className="italic text-gold">Then commit.</span>
          </h2>
          <p className="text-ivory/55 text-base sm:text-lg leading-relaxed">
            Stop guessing from a paint chip. Our studio previews real colors on your actual walls — preserving lighting, shadows, and texture.
          </p>
        </RevealOnScroll>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 border-t border-gold/15">
          {steps.map((s, i) => (
            <div
              key={s.title}
              className={`p-8 border-b border-gold/15 ${i !== steps.length - 1 ? "lg:border-r border-gold/15" : ""} flex flex-col gap-4`}
            >
              <div className="flex items-center justify-between">
                <s.icon className="size-5 text-gold" strokeWidth={1.25} />
                <span className="font-serif italic text-gold/60 text-lg">0{i + 1}</span>
              </div>
              <h3 className="font-serif text-xl">{s.title}</h3>
              <p className="text-xs text-ivory/45 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-6">
          <GoldCta to="/studio">
            <Sparkles className="size-4" />
            Open the Studio
          </GoldCta>
          <p className="text-[11px] uppercase tracking-[0.28em] text-ivory/40">No sign-up to try</p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------ Before / After section ---------------------- */
function BeforeAfter() {
  const bullets = [
    "Surgical-grade prep: caulk, sand, prime",
    "Premium Sherwin-Williams & Benjamin Moore coatings",
    "Clean cut lines, no roller stipple, no overspray",
    "Daily site cleanup; furniture protected and replaced",
  ];
  return (
    <section className="py-24 sm:py-40 px-6">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <RevealOnScroll className="space-y-6">
          <SectionMarker number="03" label="Before / After" />
          <h2 className="font-serif text-4xl sm:text-6xl leading-[1.02] italic">
            The difference is in the prep.
          </h2>
          <p className="text-ivory/55 text-base sm:text-lg leading-relaxed max-w-lg">
            Every project is sanded, patched, and primed to a finish-ready surface before a drop of color lands. Drag the handle to see real Wisconsin work.
          </p>
          <ul className="space-y-4 text-sm text-ivory/75 pt-2">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-4">
                <span className="mt-3 h-px w-6 bg-gold shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </RevealOnScroll>
        <RevealOnScroll delay={120} className="lg:pl-6">
          <div className="relative border border-gold/30 p-2">
            <BeforeAfterSlider before={beforeAsset} after={afterAsset} />
            <div className="absolute top-4 left-4 z-10">
              <span className="bg-noir/90 text-gold px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] border border-gold/30">
                Latest — Madison Residence
              </span>
            </div>
          </div>
          <p className="text-[10px] text-ivory/40 uppercase tracking-[0.28em] mt-4 text-center">Drag to compare · representative project</p>
        </RevealOnScroll>
      </div>
    </section>
  );
}

/* --------------------------- Selected Works --------------------------- */
function SelectedWorks() {
  const picks = PROJECTS.slice(0, 2);
  return (
    <section className="bg-noir-2 py-24 sm:py-40 px-6 border-y border-white/5">
      <div className="max-w-7xl mx-auto">
        <RevealOnScroll className="flex items-center gap-6 mb-16">
          <h2 className="font-serif italic text-4xl sm:text-5xl">Selected Works</h2>
          <div className="flex-grow h-px bg-white/10" />
          <Link
            to="/portfolio"
            className="text-[10px] uppercase tracking-[0.3em] text-gold hover:text-gold-hi transition-colors inline-flex items-center gap-2"
          >
            View Archive <ArrowRight className="size-3" />
          </Link>
        </RevealOnScroll>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {picks.map((p, i) => (
            <RevealOnScroll key={p.id} delay={i * 120}>
              <Link to="/portfolio" className="block space-y-5 group">
                <div className="aspect-[4/5] overflow-hidden border border-white/10">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-1000"
                  />
                </div>
                <div className="flex justify-between items-baseline">
                  <h3 className="font-serif italic text-2xl sm:text-3xl">{p.title}</h3>
                  <span className="text-[10px] uppercase tracking-[0.28em] text-ivory/45">{p.surface} · {p.city}</span>
                </div>
              </Link>
            </RevealOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------- Meet Michael --------------------------- */
function MeetMichael() {
  return (
    <section className="py-24 sm:py-40 px-6">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <RevealOnScroll className="order-2 lg:order-1 space-y-6">
          <SectionMarker number="04" label="Meet the Owner" />
          <h2 className="font-serif italic text-6xl sm:text-8xl leading-none">Michael.</h2>
          <p className="text-ivory/60 text-base sm:text-lg leading-relaxed">
            With over 30 years of hands-on experience, Michael has built Michael's Painting into one of south-central Wisconsin's most trusted painting and finishing companies.
          </p>
          <p className="text-ivory/55 text-base leading-relaxed">
            His commitment to quality workmanship, honest communication, and treating every property with respect is the foundation of everything we do — from the first call to the final brushstroke.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 text-gold hover:text-gold-hi transition-colors text-xs uppercase tracking-[0.3em] pt-2 border-b border-gold/40 pb-1"
          >
            The full story <ArrowRight className="size-3" />
          </Link>
        </RevealOnScroll>
        <RevealOnScroll delay={100} className="order-1 lg:order-2 flex justify-center">
          <div className="relative border border-gold/30 p-3 bg-noir-2">
            <img
              src={michaelOwner.url}
              alt="Michael, owner of Michael's Painting"
              loading="lazy"
              className="w-full max-w-sm lg:max-w-md object-contain"
            />
            <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-noir px-3 text-[10px] uppercase tracking-[0.3em] text-gold">Est. 30 Years</span>
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
}

/* --------------------- Testimonial + Warranty ---------------------- */
function TestimonialWarranty() {
  return (
    <section className="py-24 sm:py-40 px-6 max-w-5xl mx-auto text-center">
      <RevealOnScroll className="flex flex-col items-center">
        <div className="w-12 h-12 border border-gold flex items-center justify-center mb-10 rotate-45">
          <div className="-rotate-45 font-serif italic text-gold text-xl leading-none">"</div>
        </div>
        <blockquote className="font-serif italic text-3xl md:text-5xl leading-[1.15] text-ivory/95 max-w-4xl">
          "Michael and his crew handled our historic renovation with surgical precision. The attention to detail and professional finish were beyond what we thought possible."
        </blockquote>
        <cite className="mt-10 block text-[10px] uppercase tracking-[0.35em] text-ivory/45 not-italic">
          — David & Sarah Richardson, Milwaukee WI
        </cite>
      </RevealOnScroll>

      <div className="mt-24 pt-24 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <RevealOnScroll className="text-left space-y-5">
          <SectionMarker number="05" label="The Warranty" />
          <h3 className="font-serif italic text-4xl sm:text-5xl">The Gold Standard.</h3>
          <p className="text-ivory/55 font-light leading-relaxed max-w-md">
            Every project is backed by our signature three-year craftsmanship guarantee. We don't just paint surfaces — we protect investments with professional durability, in writing, on every job.
          </p>
        </RevealOnScroll>
        <RevealOnScroll delay={120} className="flex justify-center md:justify-end">
          <div className="relative w-56 h-56 rounded-full border border-gold/30 flex items-center justify-center p-2">
            <div className="w-full h-full rounded-full border border-gold flex flex-col items-center justify-center">
              <span className="font-serif italic text-5xl leading-none text-gold">3</span>
              <span className="text-[10px] uppercase tracking-[0.28em] mt-1 text-ivory/70">Year Quality</span>
              <div className="h-px w-8 bg-gold my-2" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-ivory/60">Assurance</span>
            </div>
            <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-noir px-3 text-[10px] text-gold uppercase tracking-[0.35em]">Certified</span>
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
}

/* ---------------------------- Estimate band --------------------------- */
function EstimateBand() {
  return (
    <section className="border-y border-white/5 bg-noir-2 py-24 sm:py-40 px-6">
      <div className="max-w-5xl mx-auto">
        <RevealOnScroll className="space-y-6 mb-12">
          <SectionMarker number="06" label="Instant Estimate" />
          <h2 className="font-serif text-4xl sm:text-6xl leading-[1.02] max-w-3xl">
            A real price range in <span className="italic text-gold">under two minutes.</span>
          </h2>
          <p className="text-ivory/55 text-base sm:text-lg leading-relaxed max-w-2xl">
            Answer five quick questions about your project and we'll calculate a transparent range — gallons, labor hours, and total — using the same formulas our estimators use on-site.
          </p>
        </RevealOnScroll>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border border-gold/15 mb-12">
          {["Room-level scope", "Surface condition", "Paint preferences"].map((s, i) => (
            <div
              key={s}
              className={`p-6 ${i !== 2 ? "sm:border-r border-b sm:border-b-0 border-gold/15" : ""}`}
            >
              <div className="text-[10px] text-gold uppercase tracking-[0.3em] mb-2 font-serif italic">Step 0{i + 1}</div>
              <div className="text-sm text-ivory">{s}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <GoldCta to="/estimate">
            <Calculator className="size-4" />
            Get My Instant Estimate
          </GoldCta>
          <Link to="/studio" className="text-xs uppercase tracking-[0.3em] text-ivory/60 hover:text-gold transition-colors">
            Or preview colors first →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Final CTA ----------------------------- */
function FinalCTA() {
  return (
    <section className="relative overflow-hidden py-32 sm:py-48 bg-gradient-to-b from-noir to-[oklch(0.10_0_0)]">
      <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center relative z-10">
        <RevealOnScroll className="space-y-10">
          <h2 className="font-serif text-6xl sm:text-8xl lg:text-9xl leading-[0.95]">
            Ready to <span className="italic text-gold">begin?</span>
          </h2>
          <p className="max-w-xl mx-auto text-ivory/55 text-base sm:text-lg font-light">
            Consult with our master painters and transform your property into a masterpiece of color, texture, and lasting protection.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-4">
            <GhostGoldCta to="/contact">Schedule Consultation</GhostGoldCta>
            <Link
              to="/studio"
              className="inline-flex items-center gap-2 text-ivory/70 hover:text-gold transition-colors text-xs uppercase tracking-[0.3em]"
            >
              <Sparkles className="size-3.5" />
              Try the Studio
            </Link>
          </div>
        </RevealOnScroll>
      </div>

      <div className="mt-24 sm:mt-32 overflow-hidden w-full opacity-25 pointer-events-none select-none">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((k) => (
            <span
              key={k}
              className="font-serif italic text-[9rem] sm:text-[12rem] leading-none tracking-tighter text-stroke-ivory pr-16 shrink-0"
            >
              Michael's Painting · Michael's Painting · Michael's Painting ·
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
